import * as React from 'react';
import PropTypes from 'prop-types';

import ReactComponentType from '@configuratorware/configurator-frontendgui/Shared/PropTypes/ReactComponent';
import withWidth, { isWidthUp, isWidthDown } from '@material-ui/core/withWidth';

import Header from '@configuratorware/configurator-frontendgui/App/Shared/Containers/Header';
import DesignAreaControlbox from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Containers/DesignAreaControlbox';
import EditDesignButton from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Containers/EditDesignButton';
import EditDesignButtonComponent from '../Component/EditDesignButton';
import CloseGraphicsEditorButton from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Containers/CloseGraphicsEditorButton';
import CloseDesignEditor from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Containers/CloseDesignEditor';
import ImageEditDialogContainer from '../Container/ImageEditDialog';

import {
  DefaultCanvasProvider,
  DefaultCanvasStateProvider,
  DefaultTextEditorStateProvider,
} from '@configuratorware/configurator-frontendgui/App/Shared/Providers/DefaultCanvasProvider';
import { DesignerAlignTarget } from '@configuratorware/configurator-frontendgui/App/Modules/Visualization/DesignerAlignTarget';
import DefaultLayout from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Layouts/DefaultLayout';
import { PopupArea as DefaultPopupArea } from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Layouts/PopupAreaProvider';
import { TwoSideBottomBar } from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Layouts/Mobile/TwoSideBottomBar';

import Designer from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Containers/Designer';
import DesignerSelectionToolbox from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Containers/DesignerSelectionToolbox';
import ScreenPropTypes from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/ScreenPropTypes';
import FullScreenView from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Components/FullScreenView/FullScreenView';
import { PreviewButton } from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/Components/FullScreenView';
import DesignerClickAwayListener from '@configuratorware/configurator-frontendgui/App/Modules/Designer/MultilayerComponents/DesignerClickAwayListener';
import DesignAreaControlboxComponent from '../Component/DesignAreaControlbox';
import Loader from '../Component/Loader/Loader';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import ImageIcon from '../Resources/Icons/ImageIcon';
import AddVisualElement from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Containers/AddVisualElement';
import TextFormatIcon from '../Resources/Icons/TextFormatIcon';
import { CustomDesigner } from './DesignerUI';
import TextControlBox from '../Component/TextControlBox';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
  labelTextMobile: {
    minWidth: 120,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    fontSize: 15,
    margin: '0 14px !important',
    width: 'auto !important',
  },
}));

/**
 * Main Screen of the configurator
 */
export function DesignerScreen(props) {
  const {
    imageOnly,
    showDesigner,
    showProductViewButton,
    fallbackMode,
    is3DvisualMode,
    isPortraitMode,
    renderHeader,
    renderSaveConfiguration,
    renderSendEmail,
    renderPdfDownload,
    renderLoadConfiguration,
    renderError,
    renderAlertMessages,
    renderConfirm,
    renderAmountPrice,
    renderDesigner,
    renderDesignerSelectionToolbox,
    renderDesignerFallback,
    renderVisualization,
    renderDesignAreaControlbox,
    renderEditDesignButton,
    renderCloseGraphicsEditorButton,
    renderImageEditDialog,
    renderCloseDesignEditor,
    renderLoader,
    renderLicenseNotice,
    customElements,
    visualizationOverlayMode,

    // Components
    CanvasProvider,
    CanvasStateProvider,
    TextEditorStateProvider,
    Layout,
    PopupArea,
    FallbackVisualization,
  } = props;

  const customClasses = useStyles();

  return (
    <CanvasProvider>
      <Layout
        imageOnly={imageOnly}
        isPortraitMode={isPortraitMode}
        renderHeader={(renderProps) =>
          renderProps.width === 'xs' && showDesigner ? () => <div /> : renderHeader()
        }
        fallbackMode={fallbackMode}
        showDesigner={showDesigner}
        visualizationOverlayMode={visualizationOverlayMode}
        renderDesigner={
          visualizationOverlayMode &&
          ((props) => (
            <FallbackVisualization fallbackMode={fallbackMode} showContent={showDesigner}>
              {showDesigner && renderDesigner({ visualizationOverlayMode, ...props })}
            </FallbackVisualization>
          ))
        }
        renderVisualization={() =>
          renderVisualization({
            fullScreenViewProps: {
              Component: FullScreenView,
              showPreviewButton: !showDesigner,
              renderTopPreviewButton: ({ width, areAllDesignAreasEmpty, ...props }) =>
                isWidthDown('sm', width) &&
                !areAllDesignAreasEmpty &&
                !showDesigner && <PreviewButton {...props} top />,
            },
          })
        }
        renderMain={(renderProps) => (
          <>
            {renderProps.width !== 'xs' && isPortraitMode ? (
              <div className={renderProps.classes.graphicsEditorWrapper}>
                {showProductViewButton && renderCloseGraphicsEditorButton(renderProps)}
                {!showDesigner && renderEditDesignButton({ styledButton: false })}
              </div>
            ) : null}
            {renderProps.width === 'xs'
              ? showDesigner
                ? renderDesignAreaControlbox({ ...renderProps, showDesigner })
                : !showProductViewButton && renderEditDesignButton({ styledButton: false })
              : null}
            {!fallbackMode && <DesignerAlignTarget />}
            {!visualizationOverlayMode && (
              <FallbackVisualization fallbackMode={fallbackMode} showContent={showDesigner}>
                {showDesigner && renderDesigner()}
              </FallbackVisualization>
            )}
            {renderError()}
            {renderAlertMessages()}
            {renderConfirm()}
            {renderSaveConfiguration()}
            {renderSendEmail()}
            {renderPdfDownload()}
            {renderLoadConfiguration()}
            {renderImageEditDialog()}
            {renderProps.width !== 'xs' && showDesigner && is3DvisualMode && renderCloseDesignEditor()}
            {customElements.main}
          </>
        )}
        renderSidebar={(renderProps) => (
          <CanvasStateProvider>
            <TextEditorStateProvider>
              <PopupArea>
                {renderProps.width !== 'xs' && isPortraitMode ? (
                  <TwoSideBottomBar
                    leftSide={
                      showDesigner && (
                        <React.Suspense fallback={renderDesignerFallback()}>
                          {renderDesignerSelectionToolbox()}
                        </React.Suspense>
                      )
                    }
                    rightSide={
                      <React.Fragment>
                        {renderDesignAreaControlbox({ ...renderProps, showDesigner })}
                        {renderAmountPrice(props)}
                      </React.Fragment>
                    }
                  />
                ) : (
                  <React.Fragment>
                    {renderProps.width !== 'xs' ? (
                      showDesigner ? (
                        <React.Fragment>
                          {renderCloseGraphicsEditorButton(renderProps)}
                          {renderDesignAreaControlbox({ ...renderProps, showDesigner })}
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          {showProductViewButton && renderCloseGraphicsEditorButton(renderProps)}
                          {renderEditDesignButton({ styledButton: false })}
                        </React.Fragment>
                      )
                    ) : null}
                    {showDesigner ? (
                      <React.Suspense fallback={renderDesignerFallback()}>
                        {renderDesignerSelectionToolbox()}
                        {renderProps.width === 'xs' && (
                          <React.Fragment>
                            <AddVisualElement
                              autoHide
                              hasLastEditedDesignArea={true}
                              renderAddImageLabel={() => (
                                <React.Fragment>
                                  <ImageIcon viewBox="0 0 30 30" isMobile />
                                  <b className={customClasses.labelTextMobile}>
                                    {t('addVisualElement.image')}
                                  </b>
                                </React.Fragment>
                              )}
                              renderAddTextLabel={() => (
                                <React.Fragment>
                                  <TextFormatIcon viewBox="0 0 30 30" isMobile />
                                  <b className={customClasses.labelTextMobile}>
                                    {t('addVisualElement.text')}
                                  </b>
                                </React.Fragment>
                              )}
                            />
                            {is3DvisualMode && renderCloseDesignEditor()}
                          </React.Fragment>
                        )}
                      </React.Suspense>
                    ) : (
                      renderProps.width === 'xs' &&
                      props.activeDesignArea &&
                      renderEditDesignButton({ styledButton: true })
                    )}
                    <div className={renderProps.classes.toBottom}>
                      {renderProps.width !== 'xs'
                        ? renderAmountPrice(props)
                        : props.activeDesignArea
                          ? renderCloseGraphicsEditorButton(renderProps)
                          : renderAmountPrice(props)}
                    </div>
                  </React.Fragment>
                )}
              </PopupArea>
            </TextEditorStateProvider>
          </CanvasStateProvider>
        )}
        renderLoader={renderLoader}
        renderLicenseNotice={renderLicenseNotice}
      />
    </CanvasProvider>
  );
}

DesignerScreen.propTypes = {
  ...ScreenPropTypes,

  showProductViewButton: PropTypes.bool,
  showPreviewButton: PropTypes.bool,
  area: PropTypes.string,
  customElements: PropTypes.shape({
    main: PropTypes.node,
  }),

  // Render props
  renderDesignerSelectionToolbox: PropTypes.func,
  renderVisualizationEdit: PropTypes.func,
  renderDesignerFallback: PropTypes.func,

  renderHeader: PropTypes.func,
  renderDesignAreaControlbox: PropTypes.func,
  renderEditDesignButton: PropTypes.func,
  renderCloseGraphicsEditorButton: PropTypes.func,
  renderImageEditDialog: PropTypes.func,
  renderCloseDesignEditor: PropTypes.func,

  // Components
  CanvasProvider: ReactComponentType.isRequired,
  CanvasStateProvider: ReactComponentType.isRequired,
  TextEditorStateProvider: ReactComponentType.isRequired,
  Layout: ReactComponentType.isRequired,
  PopupArea: ReactComponentType.isRequired,
  imageOnly: PropTypes.bool,
};

const CustomAddVisualElement = withWidth()(
  ({ width }) =>
    isWidthUp('sm', width) && ( // desktop only, for mobile it's rendered in the sidebar, see Screen.js
      <AddVisualElement
        hasLastEditedDesignArea={true}
        renderAddImageLabel={({ classes }) => (
          <React.Fragment>
            <ImageIcon viewBox="0 0 30 30" />
            <b className={classes.labelText}>{t('addVisualElement.image')}</b>
          </React.Fragment>
        )}
        renderAddTextLabel={({ classes }) => (
          <React.Fragment>
            <TextFormatIcon viewBox="0 0 30 30" />
            <b className={classes.labelText}>{t('addVisualElement.text')}</b>
          </React.Fragment>
        )}
      />
    )
);
// eslint-disable-next-line react/prop-types
const renderDesignAreaControlbox = ({ area, showDesigner }) => {
  return (
    <>
      <DesignAreaControlbox boxed={area === 'sidebar'} Component={DesignAreaControlboxComponent} />
      {showDesigner && <CustomAddVisualElement />}
    </>
  );
};

// eslint-disable-next-line react/prop-types
const renderEditDesignButton = ({ styledButton }) => (
  <EditDesignButton buttonXS={styledButton} Component={EditDesignButtonComponent} />
);

DesignerScreen.defaultProps = {
  // Render props
  renderCreator: () => null,
  renderDesigner: ({ designerClassName, visualizationOverlayMode } = {}) => {
    const designer = (
      <Designer visualizationOverlayMode={visualizationOverlayMode} Component={CustomDesigner} />
    );
    return (
      <React.Suspense fallback={<div>Loading Designer Component...</div>}>
        {visualizationOverlayMode ? (
          <>
            <DesignerClickAwayListener />
            <div className={designerClassName}>{designer}</div>
          </>
        ) : (
          designer
        )}
      </React.Suspense>
    );
  },
  renderDesignerSelectionToolbox: () => (
    <React.Suspense fallback={<div>Loading Designer Toolbox...</div>}>
      <DesignerSelectionToolbox TextControlboxComponent={TextControlBox} />
    </React.Suspense>
  ),
  renderHeader: () => <Header />,
  renderDesignerFallback: () => <div>Loading Designer Component...</div>,
  renderCloseGraphicsEditorButton: () => <CloseGraphicsEditorButton />,
  renderImageEditDialog: () => <ImageEditDialogContainer ProgressComponent={Loader} />,
  renderCloseDesignEditor: () => <CloseDesignEditor />,
  renderEditDesignButton,
  renderDesignAreaControlbox,

  // Components
  CanvasProvider: DefaultCanvasProvider,
  CanvasStateProvider: DefaultCanvasStateProvider,
  TextEditorStateProvider: DefaultTextEditorStateProvider,
  Layout: DefaultLayout,
  PopupArea: DefaultPopupArea,
};
